import { useEffect, useState } from 'react';
import { memberApi, type Member } from '../../api';
import Select from '../../components/common/Select';
import { humaniseRole, humaniseStatus } from '../../lib/labels';

const ROLES = ['super_admin', 'admin', 'advocate', 'pupil', 'clerk', 'accountant', 'viewer'] as const;

export default function SettingsMembers() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<typeof ROLES[number]>('advocate');
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await memberApi.list();
      setMembers(r.members);
    } catch (err) { setError((err as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true); setError(null); setInviteUrl(null);
    try {
      const r = await memberApi.invite({ email, role });
      setInviteUrl(r.invite_url);
      setEmail('');
      load();
    } catch (err) { setError((err as Error).message); }
    finally { setInviting(false); }
  };

  const isTrue = (v: unknown) => v === true || v === 'true';

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <div>
        <h1>Members</h1>
        <p className="muted">Manage who has access to your chambers.</p>
      </div>

      <div className="card stack">
        <h3 style={{ margin: 0 }}>Invite a new member</h3>
        <form onSubmit={invite} className="row">
          <input type="email" className="grow" placeholder="email@chambers.co.za" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Select
            value={role}
            onChange={(v) => setRole(v as typeof ROLES[number])}
            style={{ width: 180 }}
            searchPlaceholder="Search roles…"
            options={ROLES.map((r) => ({ value: r, label: humaniseRole(r) }))}
          />
          <button type="submit" className="primary" disabled={inviting || !email}>{inviting ? 'Sending…' : 'Send invite'}</button>
        </form>
        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        {inviteUrl && (
          <div style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', padding: 12, borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>Invite link — copy and send via any channel:</div>
            <code style={{ userSelect: 'all', fontSize: 12 }}>{inviteUrl}</code>
          </div>
        )}
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }} className="grow">Team ({members.length})</h3>
          <button onClick={load}>Refresh</button>
        </div>
        {loading ? <div className="muted">Loading…</div> : members.length === 0 ? (
          <p className="muted">No members yet.</p>
        ) : (
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th style={{ width: 120 }}></th></tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.ROWID}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={m.display_name || m.email} />
                      <span>{m.display_name}</span>
                      {isTrue(m.is_owner) && <span className="pill accent">Owner</span>}
                    </div>
                  </td>
                  <td>{m.email}</td>
                  <td>
                    <span>{humaniseRole(m.role)}</span>
                    {isTrue(m.is_super_admin) && <span className="pill accent" style={{ marginLeft: 6 }}>Super admin</span>}
                  </td>
                  <td><span className="pill">{humaniseStatus(m.status)}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {!isTrue(m.is_owner) && (
                      <button onClick={async () => {
                        if (!confirm(`Remove ${m.display_name || m.email}?`)) return;
                        await memberApi.remove(m.ROWID); load();
                      }}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: 'linear-gradient(135deg, #C9A34A, #A8842F)',
      color: '#1A1305', fontWeight: 700, fontSize: 11,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>{initials}</div>
  );
}
